import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Menu, Plus, LogOut } from "lucide-react"
import { useAuthStore } from "@/store/authStore"
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn, getInitials } from "@/lib/utils"
import { convencion } from "@/lib/pais"
import { navFor, todosLosItems, activoEn } from "../nav/navConfig"
import ThemePicker from "../ThemePicker"

/**
 * Barra inferior del teléfono. Asesor: Inicio · Cotizar · [+] · Clientes · Más. El [+] central abre
 * una cotización nueva desde cualquier pantalla (es lo que más hace el asesor). «Más» es UNA hoja
 * inferior con el resto del menú, la cuenta y el tema; antes el encabezado y esta barra abrían cada
 * uno su propio cajón lateral con lo mismo.
 */
export default function BottomNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, partner, logout } = useAuthStore()
  const [masAbierto, setMasAbierto] = useState(false)

  const def = navFor(partner)
  const rutas = todosLosItems(def).map((i) => i.path)
  const isActive = activoEn(location.pathname, rutas)
  const rutasPestanas = def.tabs.filter((t) => !("fab" in t)).map((t) => t.path)
  const enPestana = rutasPestanas.some((p) => isActive(p))
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ")

  const ir = (path: string) => {
    setMasAbierto(false)
    navigate(path)
  }

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-30 md:hidden border-t border-line bg-card/95 backdrop-blur-md pb-safe"
        aria-label="Navegación principal"
      >
        <ul className="flex items-end">
          {def.tabs.map((t) => {
            if ("fab" in t) {
              return (
                <li key="fab" className="flex-1 flex justify-center">
                  <button
                    type="button"
                    onClick={() => ir(t.path)}
                    aria-label={t.label}
                    className="-mt-5 mb-1 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white shadow-lg shadow-primary/30 active:scale-95 transition-transform"
                  >
                    <Plus size={26} />
                  </button>
                </li>
              )
            }
            const active = isActive(t.path)
            return (
              <li key={t.path} className="flex-1">
                <button
                  type="button"
                  onClick={() => ir(t.path)}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex w-full flex-col items-center justify-center gap-0.5 min-h-[58px] transition-colors",
                    active ? "text-primary dark:text-primary-300" : "text-subtle"
                  )}
                >
                  {t.icon}
                  <span className={cn("text-micro tracking-wide", active ? "font-semibold" : "font-medium")}>{t.label}</span>
                </button>
              </li>
            )
          })}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setMasAbierto(true)}
              aria-current={!enPestana && rutas.some((p) => isActive(p)) ? "page" : undefined}
              className={cn(
                "flex w-full flex-col items-center justify-center gap-0.5 min-h-[58px] transition-colors",
                !enPestana && rutas.some((p) => isActive(p)) ? "text-primary dark:text-primary-300" : "text-subtle"
              )}
            >
              <Menu size={22} />
              <span className="text-micro font-medium tracking-wide">Más</span>
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={masAbierto} onOpenChange={setMasAbierto}>
        <SheetContent side="bottom" className="pb-safe">
          <div className="flex items-center gap-3 px-5 pt-2 pb-3 border-b border-line">
            <Avatar className="h-11 w-11">
              <AvatarImage src={partner?.logoUrl || undefined} alt={fullName} />
              <AvatarFallback>{getInitials(fullName || user?.email)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <SheetTitle className="font-sans text-body font-semibold truncate">{fullName || user?.email}</SheetTitle>
              <SheetDescription className="text-caption text-subtle truncate">
                {partner?.organizationType === "cd2b_consultant"
                  ? `${partner?.rolCd2b === "gerente" ? "Gerente de país" : "Asesor"}${convencion(partner?.country).nombre ? ` · ${convencion(partner?.country).nombre}` : ""}`
                  : partner?.organizationName || "Partner"}
              </SheetDescription>
            </div>
          </div>
          <div className="overflow-y-auto px-3 pb-4">
            {def.groups.map((g) => (
              <div key={g.key} className="mt-3">
                <p className="px-2 mb-1 text-micro uppercase tracking-wider text-subtle">{g.label}</p>
                <ul className="grid grid-cols-2 gap-1">
                  {g.items.map((it) => {
                    const active = isActive(it.path)
                    return (
                      <li key={it.path}>
                        <button
                          type="button"
                          onClick={() => ir(it.path)}
                          aria-current={active ? "page" : undefined}
                          className={cn(
                            "flex w-full items-center gap-2.5 rounded-md px-2.5 min-h-[48px] text-left text-body-sm font-medium",
                            active ? "bg-primary-50 text-primary-700 dark:bg-primary/20 dark:text-primary-100" : "text-foreground hover:bg-surface-muted"
                          )}
                        >
                          <span className={active ? "text-primary dark:text-primary-300" : "text-subtle"}>{it.icon}</span>
                          <span className="truncate">{it.text}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
            <div className="mt-3">
              <p className="px-2 mb-1 text-micro uppercase tracking-wider text-subtle">Mi cuenta</p>
              <ul className="grid grid-cols-2 gap-1">
                {def.cuenta.map((it) => (
                  <li key={it.path}>
                    <button type="button" onClick={() => ir(it.path)} className="flex w-full items-center gap-2.5 rounded-md px-2.5 min-h-[48px] text-left text-body-sm font-medium text-foreground hover:bg-surface-muted">
                      <span className="text-subtle">{it.icon}</span>
                      <span className="truncate">{it.text}</span>
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    onClick={async () => { setMasAbierto(false); await logout(); navigate("/login") }}
                    className="flex w-full items-center gap-2.5 rounded-md px-2.5 min-h-[48px] text-left text-body-sm font-medium text-danger-700 dark:text-danger-300 hover:bg-surface-muted"
                  >
                    <LogOut size={18} /> Cerrar sesión
                  </button>
                </li>
              </ul>
            </div>
            <div className="mt-4 px-2">
              <p className="mb-1.5 text-micro uppercase tracking-wider text-subtle">Tema</p>
              <ThemePicker />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
